// src/components/VideoPreview.js
import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';

const VideoPreview = ({ file, token }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoSrc, setVideoSrc] = useState(null);

  useEffect(() => {
    if (!file || !file.path.endsWith('.mp4')) return;

    const fetchVideo = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `https://drive.suprsketch.com/api/fs/stream/${encodeURIComponent(file.path)}?bucket=${file.bucket}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        
        // Clean up old URL before setting new one
        if (videoSrc) {
          URL.revokeObjectURL(videoSrc);
        }
        
        setVideoSrc(url);
        setLoading(false);

      } catch (error) {
        // console.error('Error fetching video:', error);
        setError('Failed to load video preview');
        setLoading(false);
      }
    };

    fetchVideo();

    // Cleanup
    return () => {
      if (videoSrc) {
        URL.revokeObjectURL(videoSrc);
      }
    };
  // Remove videoSrc from dependencies as it would cause unnecessary re-fetches
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, token]);

  if (!file || !file.path.endsWith('.mp4')) return null;

  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%',
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center',
        position: 'relative',
        bgcolor: 'background.paper',
        minHeight: '400px',
      }}
    >
      {loading && (
        <Box 
          sx={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1
          }}
        >
          <CircularProgress />
        </Box>
      )}
      
      {error && (
        <Alert severity="error" sx={{ width: '80%', m: 2 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && videoSrc && (
        <video
          controls
          autoPlay={false}
          style={{ 
            width: '100%',
            height: '100%',
            maxHeight: '600px',
            objectFit: 'contain'
          }}
          onError={(e) => {
            // console.error('Video playback error:', e);
            setError('Failed to play video');
          }}
        >
          <source src={videoSrc} type="video/mp4" />
          <Typography color="text.secondary">
            Your browser does not support video playback.
          </Typography>
        </video>
      )}
    </Box>
  );
};

export default VideoPreview;